import React, { useState, useRef, useEffect } from 'react'
import { Check, X, Edit2 } from 'lucide-react'

interface EditableCellProps {
  value: any
  columnType: string
  isEditing: boolean
  onEdit: () => void
  onSave: (newValue: any) => void
  onCancel: () => void
  className?: string
}

export const EditableCell: React.FC<EditableCellProps> = ({
  value,
  columnType,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  className = ''
}) => {
  const [editValue, setEditValue] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing) {
      setEditValue(String(value || ''))
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing, value])

  const handleSave = () => {
    let processedValue: any = editValue

    // 型に応じて値を変換
    switch (columnType) {
      case 'INTEGER':
        processedValue = parseInt(editValue) || 0
        break
      case 'REAL':
        processedValue = parseFloat(editValue) || 0
        break
      case 'BOOLEAN':
        processedValue = editValue.toLowerCase() === 'true' || editValue === '1'
        break
      case 'DATETIME':
        const date = new Date(editValue)
        if (isNaN(date.getTime())) {
          alert('有効な日付を入力してください')
          return
        }
        processedValue = date.toISOString()
        break
      default:
        processedValue = editValue
    }

    onSave(processedValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      onCancel()
    }
  }

  const formatValue = (val: any, type: string): string => {
    if (val === null || val === undefined) return ''
    
    switch (type) {
      case 'BOOLEAN':
        return val ? 'true' : 'false'
      case 'DATETIME':
        try {
          return new Date(val).toLocaleString('ja-JP')
        } catch {
          return String(val)
        }
      case 'INTEGER':
      case 'REAL':
        return String(val)
      default:
        return String(val)
    }
  }

  if (isEditing) {
    return (
      <div className="flex items-center space-x-1">
        <input
          ref={inputRef}
          type={columnType === 'BOOLEAN' ? 'checkbox' : 'text'}
          value={columnType === 'BOOLEAN' ? undefined : editValue}
          checked={columnType === 'BOOLEAN' ? editValue === 'true' : undefined}
          onChange={(e) => {
            if (columnType === 'BOOLEAN') {
              setEditValue(e.target.checked ? 'true' : 'false')
            } else {
              setEditValue(e.target.value)
            }
          }}
          onKeyDown={handleKeyDown}
          className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <button
          onClick={handleSave}
          className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
          title="保存"
        >
          <Check className="h-3 w-3" />
        </button>
        <button
          onClick={onCancel}
          className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
          title="キャンセル"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    )
  }

  return (
    <div 
      className={`flex items-center justify-between group cursor-pointer hover:bg-gray-50 px-2 py-1 rounded ${className}`}
      onClick={onEdit}
    >
      <span className="font-mono text-sm">
        {formatValue(value, columnType)}
      </span>
      <Edit2 className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  )
} 